import { useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Mail, LogOut, ShieldCheck } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { http } from "@/lib/api";
import { toast } from "sonner";

/**
 * Holding screen for recruiters whose email is unverified or whose identity
 * review is pending. They can resend the verify email or log out.
 */
export default function PendingVerification() {
  const { user, logout } = useAuth();
  const [resending, setResending] = useState(false);
  const status = user?.verification_status || (user?.email_verified ? "pending_review" : "pending_email");

  const resend = async () => {
    setResending(true);
    try {
      await http.post("/auth/resend-verify-email");
      toast.success("Verification email resent — check your inbox.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resend verification email");
    } finally {
      setResending(false);
    }
  };

  const isEmail = status === "pending_email";
  const isReview = status === "pending_review";

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
      <div className="bento-card max-w-xl w-full p-8 md:p-10" data-testid="pending-verification-card">
        <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded border border-amber-400/30 bg-amber-400/10 mb-4">
          <Clock size={11} className="text-amber-300" />
          <span className="text-[10px] font-display font-bold tracking-widest uppercase text-amber-300">
            {isEmail ? "Email Verification Pending" : "Identity Review Pending"}
          </span>
        </div>

        {isEmail ? (
          <>
            <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight leading-tight">
              Confirm your email to <span className="text-electric">continue.</span>
            </h1>
            <p className="text-sm text-white/70 mt-4 leading-relaxed">
              We sent a verification link to <strong className="text-electric">{user?.email}</strong>. Click it from any device — once confirmed, we'll review your school affiliation and activate your account (usually within 24 hours).
            </p>
            <button
              onClick={resend}
              disabled={resending}
              className="btn-primary w-full justify-center mt-6 text-xs disabled:opacity-50"
              data-testid="resend-verify-email"
            >
              <Mail size={12} /> {resending ? "Sending…" : "Resend verification email"}
            </button>
          </>
        ) : (
          <>
            <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight leading-tight">
              Email confirmed. Identity <span className="text-electric">under review.</span>
            </h1>
            <div className="mt-5 flex items-start gap-2 text-amber-300 text-sm">
              <Clock size={14} className="mt-0.5 flex-shrink-0" />
              <p>This review typically completes within <strong>24 hours</strong>.</p>
            </div>
            <p className="text-sm text-white/70 mt-4 leading-relaxed">
              We hand-check every recruiter account against published school directories. This protects our athletes and their families from non-college actors.
            </p>
            <p className="text-xs text-white/50 mt-3 leading-relaxed">
              We'll email <strong className="text-white/70">{user?.email}</strong> the moment you're approved. You can close this page — no need to wait here.
            </p>
            <div className="flex items-center gap-2 mt-6">
              <div className="text-xs text-white/40 flex items-center gap-1.5">
                <ShieldCheck size={12} className="text-electric" /> Account locked until approved
              </div>
            </div>
          </>
        )}

        <button
          onClick={() => logout().then(() => window.location.assign("/"))}
          className="btn-ghost w-full justify-center mt-4 text-xs"
          data-testid="pending-logout"
        >
          <LogOut size={12} /> Log out
        </button>

        <p className="text-[11px] text-white/30 mt-6 text-center">
          Wrong account? <Link to="/" className="text-electric hover:underline">Return home</Link>
        </p>
      </div>
    </div>
  );
}
